import { useQuery } from "@tanstack/react-query";
import { privateListsApi } from "../../api/hliLists";
import { ROLES } from "../../auth/roles";
import { hasAnyRole } from "../../utils/auth";

// A private institution in Rwanda sends HEC a list of its students; the HEC Admin forwards it to the DG,
// the DG approves it to the Minister and the Minister's approval hands it to BRD. Each decides on the
// whole list, and any of them may reject it with a reason.

export const PRIVATE_LISTS_KEY = "privateLists";

export const HLI_LISTS_PATH = "/hli/lists";
export const STAFF_LISTS_PATH = "/loans/private";
export const BRD_LISTS_PATH = "/brd/private-lists";

export const KIND_OPTIONS = [
  { value: "LOAN", label: "Loans" },
  { value: "BURSARY", label: "Bursaries" },
];
export const KIND_LABELS = Object.fromEntries(KIND_OPTIONS.map((k) => [k.value, k.label]));

export const STATES = {
  DRAFT: { label: "Draft", color: "gray" },
  SUBMITTED: { label: "With HEC", color: "yellow" },
  WITH_DG: { label: "With the DG", color: "yellow" },
  WITH_MINISTER: { label: "With the Minister", color: "blue" },
  APPROVED: { label: "Approved", color: "green" },
  REJECTED: { label: "Rejected", color: "red" },
  DECIDED: { label: "Decided", color: "gray" },
};
export const stateOf = (state) => STATES[state] ?? { label: state, color: "gray" };

// The staff's tabs: what waits on each of them, then the outcomes
export const STAFF_TABS = [
  { value: "", label: "All" },
  { value: "SUBMITTED", label: "With HEC" },
  { value: "WITH_DG", label: "With the DG" },
  { value: "WITH_MINISTER", label: "With the Minister" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

// What each decider may do on a list in the state it waits on them in; rejecting needs a reason
const DECISIONS = [
  { role: ROLES.ADMIN, state: "SUBMITTED", actions: [{ action: "FORWARD", label: "Forward to the DG" }] },
  { role: ROLES.DG, state: "WITH_DG", actions: [{ action: "APPROVE", label: "Approve to the Minister" }] },
  { role: ROLES.MINISTER, state: "WITH_MINISTER", actions: [{ action: "APPROVE", label: "Approve" }] },
];
const REJECT = { action: "REJECT", label: "Reject", reason: true, destructive: true };

// The decisions the signed-in user may take on a list now: none when it is not their turn
export const decisionsFor = (state) => {
  const turn = DECISIONS.find((d) => d.state === state && hasAnyRole(d.role));
  return turn ? [...turn.actions, REJECT] : [];
};

const STEPS = {
  CREATED: "Draft started",
  SUBMITTED: "Submitted to HEC",
  FORWARDED_TO_DG: "HEC forwarded it to the DG",
  REJECTED_BY_HEC: "Rejected by HEC",
  DG_APPROVED: "The DG approved it to the Minister",
  REJECTED_BY_DG: "Rejected by the DG",
  MINISTER_APPROVED: "The Minister approved it; sent to BRD",
  REJECTED_BY_MINISTER: "Rejected by the Minister",
};
export const stepLabel = (step) => STEPS[step] ?? step;

export const formatRwf = (amount) => `RWF ${Number(amount ?? 0).toLocaleString("en-US")}`;
export const studentsLabel = (n) => `${n.toLocaleString("en-US")} student${n === 1 ? "" : "s"}`;
export const nameOf = (e) => [e.firstName, e.lastName].filter(Boolean).join(" ");

const fetching = (what, call) => async () => {
  try {
    return (await call()).data;
  } catch (error) {
    console.error(`Failed to fetch ${what}:`, error);
    throw error;
  }
};

export const useMyLists = () => useQuery({ queryKey: [PRIVATE_LISTS_KEY, "mine"], queryFn: fetching("the institution's lists", privateListsApi.mine) });

export const useStaffLists = (state) =>
  useQuery({ queryKey: [PRIVATE_LISTS_KEY, "staff", state || "all"], queryFn: fetching(`the submitted lists (${state || "all"})`, () => privateListsApi.submitted(state)) });

export const useBrdLists = () => useQuery({ queryKey: [PRIVATE_LISTS_KEY, "brd"], queryFn: fetching("the approved lists", privateListsApi.approved) });

// One list with its students and history: the officer's own, or as staff and BRD see it
export const useList = (id, own) =>
  useQuery({
    queryKey: [PRIVATE_LISTS_KEY, own ? "mine" : "staff", id],
    enabled: Boolean(id),
    queryFn: fetching(`list ${id}`, () => (own ? privateListsApi.own(id) : privateListsApi.staffView(id))),
  });
